import axios, { AxiosInstance, AxiosResponse, CancelTokenSource } from "axios";
import jwtDecode, { JwtPayload } from "jwt-decode";
import { Dispatch } from "redux";

import { CellControlAction, CellServiceAction, CellService, ProcessEngineerControlAction, HistorianControlAction } from "@liveline/ll_node_common/lib/Permissions/types";

import { notifyFailed, notifySuccess } from "./notifyAsync";

import { authActions } from "../state/features/authReducer";
import { dispatchAppLogout } from "../state/dispatchAppLogout";
import { getSession } from "./sessionAPI";
import { getTokens, setTokens } from "./tokenStore";
import { retryWithBackoff } from "../utils/retryWithBackoff";
import type {
  LayoutDetail,
  LayoutPayload,
  EquipmentLayoutImageEntity,
} from '../components/admin/cell-layout/types';

import { Facility } from '@liveline/ll_node_common/lib/models/api/facility';

// API definitions
import { Run, ProductRunSummary } from '@liveline/ll_node_common/lib/models/api/run';
import { 
    User, 
    Role, 
    UserFacilityRole, 
    Signup, 
    CriticalTagStatsResponse, 
    FacilityUserRole, 
    FacilityCell, 
    PaginationMeta, 
    GetRunsQueryParams, 
    PhysicsSignalEntity, 
    TimeSeriesDataPoint, 
    FacilityLinePartsRulesEntity,
    // Run,
    GetRunsResponse,
    ReportingRunSummary,
    // ProductRunSummary,
    PlotTemplateEntity,
    SharedPlotTemplateEntity
} from "./apiTypes";
import {
    FacilitySignalAlertConfigurationEntity,
    AlertType,
    AlertLog,
} from "@liveline/ll_node_common/lib/models/api/alerts";
import { 
    CellRuleSet,
    Controller
} from "@liveline/ll_node_common/lib/models/api/ruleControls";
import { 
    DPK,
    DQSRunResult,
    LPPTrainingRequest, 
    LivelinePhysicsPackageRecord, 
    LPPPerformance, 
    LCPPerformance, 
    LCPTrainingRequest, 
    LivelineControlsPackageRecord,
    KeyStats, 
} from "@liveline/ll_node_common/lib/models/api/dataScience";
import { 
    EquipmentOutput,
    Sensor,
    Equipment,
    EquipmentEditable,
    EquipmentTagSimpleView,
    EquipmentTreeEntity,
    EquipmentType,
} from "@liveline/ll_node_common/lib/models/api/equipment";
import {CellProduct} from "@liveline/ll_node_common/lib/models/api/cell";
import {DbApiResponse} from "@liveline/ll_node_common/lib/models/api/general";
import { EquipmentUITemplateKafkaConnection } from "@liveline/ll_node_common/lib/models/api/uiTemplate";
/**
 * Client to connect with (customer) console_server
 */
export class APIClient {
    // region api client auth
    logOut = () => {
        dispatchAppLogout(this.dispatch, this.instance.defaults.baseURL);
    };

    public instance: AxiosInstance;
    dispatch: any;
    cancelTokenSource: CancelTokenSource;

    checkSessionExpiry = (token?: string) => {
        try {
            let res = false;
            if (token) {
                const decodedPayload: JwtPayload = jwtDecode(token);
                if (decodedPayload?.exp) {
                    const expirationTime = decodedPayload.exp;
                    const currentTime = Math.floor(Date.now() / 1000);
                    if (currentTime > expirationTime) {
                        res = true;
                    } else {
                        //console.log("not expired");
                    }
                } else {
                    res = true;
                }
            } else {
                console.log("no token");
                res = true;
            }
            return res;
        } catch (ex) {
            console.error("Exception in checkSessionExpiry - ", ex);
            return true;
        }
    };

    /**
     * Get a fresh accessToken via GET /session (LT-2998): the console server
     * holds the refresh token and refreshes against the auth service itself —
     * the browser never sees a refresh token. Logs out when the server-side
     * session is gone.
     */
    async refreshToken() {
        try {
            const session = await getSession(this.instance.defaults.baseURL!);
            if (!session?.accessToken) {
                this.logOut();
                this.cancelTokenSource.cancel(
                    "Request canceled - Session expired, please log in again."
                );
                return;
            }
            // Update the in-memory store synchronously so requests already in
            // flight through the interceptor pick up the new token
            setTokens({ accessToken: session.accessToken, idToken: session.idToken });
            this.dispatch(
                authActions.refreshToken({
                    userInfo: { accessToken: session.accessToken, idToken: session.idToken },
                })
            );
            return session.accessToken;
        } catch (error) {
            this.logOut();
            this.cancelTokenSource.cancel(`Request canceled -  ${error}`);
        }
    }

    sleep(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    // Tokens live in memory only (LT-2998) — never read them from localStorage
    getUserInfo = () => {
        const { accessToken, idToken } = getTokens();
        return { accessToken, idToken };
    };

    constructor(url: string, dispatch: Dispatch) {
        this.dispatch = dispatch;

        const userInfo = this.getUserInfo();

        // Create a CancelToken source
        this.cancelTokenSource = axios.CancelToken.source();

        this.instance = axios.create({
            baseURL: url,
            headers: {
                Authorization: `Bearer ${userInfo?.accessToken}`,
            },
            cancelToken: this.cancelTokenSource.token,
        });

        this.instance.interceptors.request.use(
            async (config: any) => {
                if (config?.headers?.Authorization) {
                    try {
                        const token = this.getUserInfo()?.accessToken;
                        config.headers.Authorization = `Bearer ${token}`; //make sure it's using the latest
                        // Missing or expired token: get a fresh one off the
                        // session cookie before letting this call out
                        const isExpired = this.checkSessionExpiry(token);
                        if (isExpired) {
                            console.log("expired and attempting to refresh");
                            const newAccessToken = await this.refreshToken();
                            config.headers.Authorization = `Bearer ${newAccessToken}`;
                            return config;
                        }
                    } catch (error) {
                        console.log("something failed", error);
                        this.logOut();
                        this.cancelTokenSource.cancel(
                            `Request canceled - ${error}`
                        );
                        //throw err
                    }
                } else {
                    this.logOut();
                    this.cancelTokenSource.cancel(
                        "Request canceled - Authorization not found!"
                    );
                    //throw errr
                }
                return config;
            },
            (error: any) => {
                this.logOut();
                this.cancelTokenSource.cancel(`Error in interceptor - ${error}`);
            }
        );

    }

    // endregion api client auth

    handlePagination = (url: string, pageNum: number) => {
        if (pageNum > 1) {
            return `${url}?page_num=${pageNum}`;
        }

        return url;
    };

    catchError = (title: string, functionName: string, error: any) => {
        if (!axios.isCancel(error)) {
            notifyFailed(
                title,
                error?.response?.data?.message || error?.message
            );
            return {};
        }
    };

    getOrigin() {
        try {
            const url = new URL(this.instance.defaults.baseURL!);
            return url.origin;
        } catch (error) {
            console.log(error);
        }
        return this.instance.defaults.baseURL;
    }

    async getServiceUrl(jobDescription: string, facilityId: number, cellId: number) {
        try {
            const job = String(jobDescription).toLowerCase();
            const facility = String(facilityId).toLowerCase();
            const cell = String(cellId).toLowerCase();
            const queryParams = { job, facility, cell };
            const response = await this.instance(`/service-url`, {
                params: queryParams,
            });
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Service URL", "getServiceUrl", error);
        }
    }

    // Removed: getCellCommunicationAction / postCellCommunicationAction / getMatrixControllerAction.
    // The first two duplicated getControllerAction / postControllerAction below on the identical
    // /cell-service-communication URL; getControllerAction additionally retries transient failures
    // and both support an AbortSignal. getMatrixControllerAction called the /matrix-controller route
    // that console_server has now unmounted — its only caller (State Action Matrix Configuration's
    // 'Load Matrices' button) uses getControllerAction with CellService.MATRIX_CONTROLLER +
    // CellServiceAction.LOAD_MATRICES, mirroring loadrules.

    /**
     * GET a cell-service action, retrying only *transient* failures with exponential
     * backoff + jitter. Unlike the old fixed-1s loop, `isTransientError` EXCLUDES a
     * confirmed 500, so a genuinely-down service is hit once instead of 3x (no self-
     * inflicted request storm). Aborts (CanceledError) are non-transient too, so the
     * caller's timeout/abort still short-circuits immediately.
     * @param facilityId
     * @param cellId
     * @param service
     * @param action
     * @param retries max retry attempts after the initial try (default 3)
     * @returns
     */
    async getControllerAction(facilityId: number, cellId: number, service: CellService, action: CellServiceAction, retries: number = 3, opts?: { signal?: AbortSignal }) {
        let url: string | undefined = undefined;
        try {
            url = `/cell-service-communication/facility/${facilityId}/cell/${cellId}/service/${service}/action/${action}`;
            const requestUrl = url;
            const response = await retryWithBackoff(
                () => this.instance.get(requestUrl, { signal: opts?.signal }),
                { retries, baseMs: 750, maxMs: 6000 },
            );
            return response?.data ?? {};
        } catch (error) {
            console.warn(`Controller: ${service}`, `getControllerAction(): Action: ${action}, url: ${url}`, error);
            //this.catchError(`Controller: ${service}`, `getControllerAction(): Action: ${action}, url: ${url}`, error);
            throw error;
        }
    }

    async postControllerAction(facilityId: number, cellId: number, service: CellService, action: CellControlAction | ProcessEngineerControlAction | HistorianControlAction, reqBody: any, opts?: { signal?: AbortSignal }) {
        try {
            const url = `/cell-service-communication/facility/${facilityId}/cell/${cellId}/service/${service}/action/${action}`;
            const response = await this.instance.post(url, reqBody, { signal: opts?.signal });
            return response?.data ?? {};
        } catch (error: any) {
            this.catchError(`Controller: ${service}`, `Action: ${action}`, error);
            return error?.status;
        }
    }

    async getImage(cellId: number, productId: number) {
        try {
            const response = await this.instance(
                `/product-images/cell/${cellId}/product/image/${productId}`
            );
            return response;
        } catch (error) {
            //his.catchError('Rules Configuration', 'getImage', error);
        }
    }

    // region Get Facility/Cell/Product APIs

    async getAllFacilities(): Promise<Facility[] | undefined>{
        try {
            const response = await this.instance.get<AxiosResponse<Facility[]>>(`/facilities/facilities`);
            return response?.data?.data;
        } catch (error) {
            this.catchError("Facilities", "getFacilities", error);
            return undefined;
        }
        
    }

    async getFacilityCells(facility_id: number) {
        try {
            const facilityId = Number(facility_id);
            const response = await this.instance.get<{ data: FacilityCell[], meta: PaginationMeta }>(
                `/cells/cells/facility/${facilityId}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Cells", "getCells", error);
        }
    }

    async getProductCellAssignedPart(part_id: number, cell_id: number, page_num: number, page_size: number) {
        try {
            const productId = Number(part_id);
            const cellId = Number(cell_id);
            const queryParams = { page_num, page_size };
            const response = await this.instance.get(
                `/products-assigned/assigned-parts/cell/${cellId}/products/${productId}`,
                { params: queryParams }
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Products", "getProductCellAssignedPart", error);
        }
    }

    async getRecordPartAttribute(
        facility_line_id: number,
        part_id: number,
        attribute_id: number,
        facility_line_assigned_parts_id: number
    ) {
        try {
            const cell_id = Number(facility_line_id);
            const queryParams = {
                part_id,
                attribute_id,
                facility_line_assigned_parts_id,
            };
            const response = await this.instance.get(
                `/part-attributes/part-attributes/cellId/${cell_id}`,
                { params: queryParams }
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Products", "getRecordPartAttribute", error);
        }
    }

    async getCellParts(cell_id: number, only_active: boolean, page_num: number) {
        try {
            const cellId = Number(cell_id);
            const queryParams = { only_active, page_num };
            const response = await this.instance.get(
                `/products-assigned/assigned-parts/cell/${cellId}/products`,
                { params: queryParams }
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Parts", "getCellAssignedParts", error);
        }
    }

    async getFacilityProducts(facility_id: number) {
        try {
            const facilityId = Number(facility_id);
            const response = await this.instance.get(
                `/products/facilityId/${facilityId}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Parts", "getFacilityProducts", error);
        }
    }

    // endregion Get Facility/Cell/Product APIs

    // region Equipment and Services APIs

    async getEquipment(cell_id: number) {
        try {
            const cellId = Number(cell_id);
            const response: AxiosResponse<DbApiResponse<Equipment>> = await this.instance.get(
                `/equipment/cell/${cellId}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Equipment", "getEquipment", error);
        }
    }

    async getLDCPServices(cell_id: number) {
        try {
            const cellId = Number(cell_id);
            const response = await this.instance.get(
                `/equipment/cell/${cellId}/ldcp`
            );
            return response?.data ?? {};
        } catch (error) {
            console.error(error);
            //this.catchError("Equipment", "getLDCPServics", error);
        }
    }

    async getEquipmentTypes(_cellId?: number) {
        try {
            const response: AxiosResponse<DbApiResponse<EquipmentType>> = await this.instance.get(
                `/equipment/meta/equipment-types`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Equipment", "getEquipmentTypes", error);
        }
    }

    async createEquipmentForCell(
        cellId: number,
        equipmentTypeId: number,
        body: { alias: string; description: string }
    ) {
        try {
            const cell_id = Number(cellId);
            const equipment_type_id = Number(equipmentTypeId);
            const response = await this.instance.post(
                `/equipment/equipment/cell/${cell_id}/service/${equipment_type_id}`,
                body
            );
            return response?.status;
        } catch (error) {
            this.catchError("Equipment", "createEquipmentForCell", error);
        }
    }

    async updateEquipment(id: number, editEquipmentRequestBody: EquipmentEditable) {
        try {
            const url = `/equipment/equipmentid?id=${id}`;
            const response = await this.instance.put(
                url,
                editEquipmentRequestBody
            );
            return response?.status;
        } catch (error) {
            this.catchError("Equipment", "updateEquipment", error);
        }
    }

    async refreshEquipment(cellId: number) {
        try {
            const url = `/equipment/cell/${cellId}/refresh`;
            const response = await this.instance.get(url);
            return response?.status;
        } catch (error) {
            this.catchError("Equipment", "updateEquipment", error);
        }
    }

    // endregion Equipment and Services APIs

    async postRulesProfileImage(cellId: number, productId: number, imageRequestBody: any) {
        try {
            const formData = new FormData();
            formData.append("file", imageRequestBody);
            const headers = {
                "Content-Type": "multipart/form-data",
                ...this.instance.defaults.headers,
            } as any;
            const response = await this.instance.post(
                `product-images/cell/${cellId}/product/image/${productId}`,
                formData,
                {
                    headers: headers,
                }
            );
            notifySuccess(
                "Complete",
                "Image uploaded successfully"
            );
            return response?.status;
        } catch (error) {
            this.catchError("Image", "postRulesProfileImage", error);
        }
    }

    async deleteImage(cellId: number, imageId: number) {
        try {
            const cell_id = Number(cellId);
            const id = Number(imageId);
            const url = `product-images/cell/${cell_id}/product/image/id/${id}`;
            const response = await this.instance.delete(url);
            notifySuccess(
                "Image Configuration",
                "Deleted Successfully!"
            );
            return response?.status;
        } catch (error) {
            this.catchError("Image Configuration", "deleteImage", error);
        }
    }

    // Region State Action Matrix APIs

    async getStateActionMatrices(cellId: number, productId: number) {
        try {
            const cell_id = Number(cellId);
            const product_id = Number(productId);
            const url = `/state_action_matrix/cell/${cell_id}/product/${product_id}`;
            const response = await this.instance.get(url);
            return response?.data ?? {};
        } catch (error) {
            this.catchError(
                "State Action Matrix Configuration",
                "getStateActionMatrices",
                error
            );
        }
    }

    async getStateActionMatrixHistory(cellId: number, matrixId: number) {
        try {
            const cell_id = Number(cellId);
            const matrix_id = Number(matrixId);
            const url = `/state_action_matrix/cell/${cell_id}/matrix/${matrix_id}/history`;
            const response = await this.instance.get(url);
            return response?.data ?? {};
        } catch (error) {
            this.catchError(
                "State Action Matrix Configuration History",
                "getStateActionMatrixHistory",
                error
            );
        }
    }

    async getStateActionMatrixHistoryByCAPID(cellId: number, cellAssignedPartId: number) {
        try {
            const cell_id = Number(cellId);
            const cell_assigned_part_id = Number(cellAssignedPartId);
            const url = `/state_action_matrix/cell/${cell_id}/cellassignedpartid/${cell_assigned_part_id}/history`;
            const response = await this.instance.get(url);
            return response?.data ?? {};
        } catch (error) {
            this.catchError(
                "State Action Matrix Configuration History by CAPID",
                "getStateActionMatrixHistoryByCAPID",
                error
            );
        }
    }

    async createStateActionMatrix(cellId: number, productId: number, requestBody: any) {
        try {
            const cell_id = Number(cellId);
            const product_id = Number(productId);
            const url = `/state_action_matrix/cell/${cell_id}/product/${product_id}`;
            const response = await this.instance.post(url, requestBody);
            return response?.status;
        } catch (error) {
            this.catchError(
                "State Action Matrix Configuration",
                "createStateActionMatrix",
                error
            );
        }
    }

    async createStateActionMatrixHistory(cellId: number, matrixId: number, requestBody: any) {
        try {
            const cell_id = Number(cellId);
            const matrix_id = Number(matrixId);
            const url = `/state_action_matrix/cell/${cell_id}/matrix/${matrix_id}/history`;
            const response = await this.instance.post(url, requestBody);
            return response?.status;
        } catch (error) {
            this.catchError(
                "State Action Matrix Configuration",
                "createStateActionMatrix",
                error
            );
        }
    }

    async updateStateActionMatrix(cellId: number, matrixId: number, requestBody: any) {
        try {
            const cell_id = Number(cellId);
            const matrix_id = Number(matrixId);
            const url = `/state_action_matrix/cell/${cell_id}/matrix/${matrix_id}`;
            const response = await this.instance.put(url, requestBody);
            return response?.status;
        } catch (error) {
            this.catchError(
                "State Action Matrix Configuration",
                "updateStateActionMatrix",
                error
            );
        }
    }

    // end

    // region Run APIs

    async getRuns(requestData: GetRunsQueryParams) {
        try {
            const {
                facility_id,
                cell_id,
                product_id,
                run_id,
                start,
                end,
                page_size,
                page_num,
                q,
                sort,
                sort_order,
                min_duration,
            } = requestData;
            const queryParams = {
                cell_id,
                product_id,
                run_id,
                start,
                end,
                page_size,
                page_num,
                q,
                sort,
                sort_order,
                min_duration,
            };

            const runList = await this.instance.get<{ data: GetRunsResponse}>(`/runs/${facility_id}`, {
                params: queryParams,
            });
            return runList?.data;
        } catch (ex) {
            this.catchError("Rules Configuration", "getRuns", ex);
        }
    }

    async getCurrentRun(facilityId: number, cellId: number) {
        try {
            const response = await this.instance.get(
                `/runs/current/cell/${cellId}`
            );
            return response?.data;
        } catch (error: any) {
            console.error("Exception in getCurrentRun- PLEASE DO SOMETHING ABOUT THIS EVENTUALLY ", error);
            // notifyFailed("Runs", error?.message);
            return [];
        }
    }

    async getRun(facilityId: number | string, runId: string) {
        // Never hit the endpoint with the NO_ACTIVE_RUN sentinel (-1) or a
        // missing id — db_api expects a real run id and returns a 500 otherwise
        const id = String(runId);
        if (!id || id === '-1' || id === 'undefined' || id === 'null') {
            throw new Error(`getRun called with invalid runId: ${runId}`);
        }
        try {
            const response = await this.instance.get(`/runs/facility/${facilityId}/run/${id}`);
            return response?.data ?? {};
        }
        catch (error) {
            // this.catchError("Runs", "getRun", error);
            throw error;
        }
    }

    // Per-measurement product spec for a run (reporting.run_critical_tags): target +
    // target_upper/lower_tolerance per equipment_data_tag_id. Best-effort (spec lines are an
    // overlay) — returns [] on a bad/missing run id or any error rather than throwing.
    async getRunCriticalTags(facilityId: number | string, runId: string): Promise<PhysicsSignalEntity[]> {
        const id = String(runId);
        if (!id || id === '-1' || id === 'undefined' || id === 'null') return [];
        try {
            const response = await this.instance.get<PhysicsSignalEntity[]>(
                `/runs/facility/${facilityId}/run/${id}/critical-tags`,
            );
            return response?.data ?? [];
        } catch (error) {
            console.warn('getRunCriticalTags failed', error);
            return [];
        }
    }

    // Process-insight controllable-output signals for a cell/product — carries numeric deadband
    // deltas (outputDeadBandUpper/Lower) for the blue inner-band lines. Best-effort → [] on error.
    async getProcessOutputSignals(cellId: number, productId: number): Promise<PhysicsSignalEntity[]> {
        if (!cellId || !productId) return [];
        try {
            const response = await this.instance.get<{ data: PhysicsSignalEntity[] }>(
                `/equipment/cell/${cellId}/process-output-signals`,
                { params: { productId } },
            );
            return response?.data?.data ?? [];
        } catch (error) {
            console.warn('getProcessOutputSignals failed', error);
            return [];
        }
    }

    async getRunDataQuality(facilityId: number | string, runId: string) {
        try {
            const response = await this.instance.get<{ data: DPK }>(`/runs/facility/${facilityId}/run/${runId}/data-quality`);
            return response?.data;
        }
        catch (error) {
            throw error;
        }
    }

    async getRunDpkInfo(facilityId: number | string, runId: string) {
        try {
            const response = await this.instance.get<{ data: string }>(`/runs/facility/${facilityId}/run/${runId}/dpk-info-zip`);
            return response?.data;
        }
        catch (error) {
            throw error;
        }
    }

    async getRunPartSummary(facilityId: number | string, daysBack: number | string) {
        try {
            const response = await this.instance.get<{ data: ProductRunSummary[] }>(`/runs/facility/${facilityId}/summary`, { params: { daysBack } });
            return response?.data;
        }
        catch (error) {
            throw error;
        }
    }

    async updateRunNotes(cellId: number | string, runId: string, body: { notes: string, isTrial: boolean }) {
        try {
            const response = await this.instance.put(
                `/runs/cell/${cellId}/run/${runId}/notes`,
                body
            );
            const responseData = response?.data;
            if (responseData?.status === "success") {
                notifySuccess("Run Notes", "Run notes updated successfully");
            }
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Runs", "updateRunNotes", error);
            throw error;
        }
    }

    async updateRunMeta(cellId: number | string, runId: string, requestBody: any) {
        try {
            const response = await this.instance.put(
                `/runs/cell/${cellId}/run/${runId}/notes`,
                requestBody
            );
            const responseData = response?.data;
            if (responseData?.status === "success") {
                notifySuccess("Event Defects", "Run Meta updated successfully");
            }
            return response?.status ?? {};
        } catch (error) {
            this.catchError("Event Defects", "updateRunMeta", error);
            throw error;
        }
    }

    // endregion Run APIs


    // region Alert Configuration APIs

    /**
     * Retrieves alert logs for a configured alert in a cell
     * @param {Number} cellId
     * @param {string} alertConfigId
     * @returns array of AlertLogResponse objects
     */
    async getAlertConfigLog(cellId: number, alertConfigId: string) {
        try {
            const url = `/alerts/cell/${cellId}/log/config/${alertConfigId}`;
            const response = await this.instance.get<{data: AlertLog[]}>(url);
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Alerts Config Log", "getAlertConfigLog", error);
        }
    }

    /**
     * Retrieves all alert logs for a cell
     * @param {*} cellId
     * @returns array of AlertLogResponse objects
     */
    async getAlertsLog(cellId: number) {
        try {
            const url = `/alerts/cell/${cellId}/log`;
            const response = await this.instance.get(url);
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Alerts Log", "getAlertsLog", error);
        }
    }

    // region Alert Configuration APIs


     async getDestinationTypes() {
        try {
            const response = await this.instance.get(
                `/alerts/destination-types`
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Alerts", "getDestinationTypes", error);
        }
    }

    async getAlertTypes() {
        try {
            const response = await this.instance.get<{data: AlertType[]}>(`/alerts/alert-types`);
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Alerts", "getAlertTypes", error);
        }
    }

    async getNumericAlertTargets(cellId: number, dataTagTypeId: number) {
        try {
            const response = await this.instance.get<{data: EquipmentTagSimpleView[]}>(
                `/equipment/equipment_tag/cell/${cellId}/numeric?dataTagTypeId=${dataTagTypeId}`
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Alerts", "getAlertTargets", error);
        }
    }

    async getRelatedTargets(cellId: number, equipmentComponentElementId: number) {
        try {
            const response = await this.instance.get<{data: EquipmentTagSimpleView[]}>(
                `/equipment/equipment_tag/cell/${cellId}/equipment_component_element/${equipmentComponentElementId}`
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Alerts", "getRelatedTargets", error);
        }
    }

    async createAlertConfiguration(cellId: number, alertConfigurationRequestBody: any) {
        try {
            const response = await this.instance.post<FacilitySignalAlertConfigurationEntity>(
                `/alerts/cell/${cellId}/config`,
                alertConfigurationRequestBody
            );
            return response;
        } catch (error) {
            this.catchError("Alerts", "createAlertConfiguration", error);
        }
    }

    async deleteAlertConfiguration(cellId: number, alertConfigId: string) {
        try {
            const response = await this.instance.delete(
                `/alerts/cell/${cellId}/config/${alertConfigId}`
            );
            return response;
        } catch (error) {
            this.catchError("Alerts", "deleteAlertConfiguration", error);
        }
    }

    async updateAlertConfiguration(
        cellId: number,
        alertConfigId: string,
        alertConfigurationRequestBody: any
    ) {
        try {
            const response = await this.instance.put(
                `/alerts/cell/${cellId}/config/${alertConfigId}`,
                alertConfigurationRequestBody
            );
            return response;
        } catch (error) {
            this.catchError("Alerts", "updateAlertConfiguration", error);
        }
    }

    async subscribeUserToAlert(
        cellId: number,
        alertConfigId: string,
        alertSubscriptionRequestBody: any
    ) {
        try {
            const response = await this.instance.post(
                `/alerts/cell/${cellId}/config/${alertConfigId}/users/subscription`,
                alertSubscriptionRequestBody
            );
            return response;
        } catch (error) {
            this.catchError("Alerts", "subscribeUserToAlerts", error);
        }
    }

    async unsubscribeUserFromAlert(cellId: number, alertConfigId: string, userId: string) {
        try {
            const response = await this.instance.delete(
                `/alerts/cell/${cellId}/config/${alertConfigId}/users/subscriptions/${userId}`
            );
            return response;
        } catch (error) {
            this.catchError("Alerts", "subscribeUserToAlerts", error);
        }
    }

    async getUsersSubscription(cellId: number, alertConfigId: string) {
        try {
            const response = await this.instance.get(
                `/alerts/cell/${cellId}/config/${alertConfigId}/users/subscription`
            );
            return response;
        } catch (error) {
            this.catchError("Alerts", "getUsersSubscription", error);
        }
    }

    async getAlertConfigurations(cellId: number) {
        try {
            const response = await this.instance.get<{ data: FacilitySignalAlertConfigurationEntity[]}>(
                `/alerts/cell/${cellId}/config`
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Alerts", "getAlertConfigurations", error);
        }
    }

    // region process monitor APIs
    /**
     *
     * @param {*} facilityId
     * @param {*} cellId
     * @returns
     */
    async getAlertStatuses(facilityId: number, cellId: number) {
        try {
            const response = await this.instance.get(
                `/process-monitor/facility/${facilityId}/cell/${cellId}/status`
            );
            return response;
        } catch (error) {
            console.error("Exception in getAlertStatuses: ", error);
        }
    }

    async getCriticalTagStats(facilityId: number, cellId: number) {
        try {
            const response = await this.instance.get(
                `/process-monitor/facility/${facilityId}/cell/${cellId}/critical-tags`
            );
            return response?.data as CriticalTagStatsResponse;
        } catch (error) {
            // Was "getAlertStatuses" — a copy-paste that sent anyone debugging this to the wrong
            // method. The stray console.log of every response goes with it.
            console.error("Exception in getCriticalTagStats: ", error);
        }
    }

    // Facility-scoped chart tolerance settings (LT-3221). Returns the settings entity
    // { id, thisFacilityId, data: { settings, rules } }, or the db_api built-in seed default.
    async fetchChartToleranceSettings(facilityId: number) {
        try {
            const response = await this.instance.get(
                `/chart-tolerance-settings/facility/${facilityId}`
            );
            return response?.data;
        } catch (error) {
            console.error("Exception in fetchChartToleranceSettings: ", error);
        }
    }

    // endregion process monitor APIs


    async reloadAlertConfigurations(facilityId: number, cellId: number) {
        try {
            const response = await this.instance.get(
                `/process-monitor/reload/facility/${facilityId}/cell/${cellId}`
            );
            return response;
        } catch (error) {
            this.catchError("Alerts", "reloadAlertConfigurations", error);
        }
    }

   

    async updateOEEMetrics(payload: any) {
        try {
            const url = `/cell-oee/updateOEEMetrics`;
            const response = await this.instance.post(url, payload);
            return response?.status;
        } catch (error) {
            this.catchError("Cell OEE Metrics", "updateOEEMetrics", error);
        }
    }

    // region Rule Configuration APIs

    async createRule(cellId: number, productId: number, newRuleRequestBody: any) {
        try {
            const cell_id = Number(cellId);
            const product_id = Number(productId);
            const url = `/rule-configs/rules/cell/${cell_id}/product/${product_id}`;
            const response = await this.instance.post(url, newRuleRequestBody);
            return response?.status;
        } catch (error) {
            this.catchError("Rules Configuration", "createRule", error);
        }
    }

    async updateRule(cellId: number, ruleId: number, editRuleRequestBody: any) {
        try {
            const cell_id = Number(cellId);
            const rule_id = Number(ruleId);
            const url = `/rule-configs/rules/cell/${cell_id}/rule/${rule_id}`;
            const response = await this.instance.put(url, editRuleRequestBody);
            return response?.status;
        } catch (error) {
            this.catchError("Rules Configuration", "updateRule", error);
        }
    }

    async createRecipe(cellId: number, productId: number, newRecipeRequestBody: any) {
        try {
            const cell_id = Number(cellId);
            const product_id = Number(productId);
            const queryParams = { product_id };
            const url = `/rule-configs/rules/cell/${cell_id}/recipe`;
            const response = await this.instance.post(url, newRecipeRequestBody, {params: queryParams});
            return response?.status;
        } catch (error) {
            this.catchError("Recipe Configuration", "createRecipe", error);
        }
    }

    async updateRecipe(cellId: number, recipeId: number, recipeRequestBody: any) {
        try {
            const cell_id = Number(cellId);
            const recipe_id = Number(recipeId);
            const url = `/rule-configs/rules/cell/${cell_id}/recipe/${recipe_id}`;
            const response = await this.instance.put(url, recipeRequestBody);
            return response?.status;
        } catch (error) {
            this.catchError("Rules Configuration", "updateRecipe", error);
        }
    }

    async deleteRule(cellId: number, ruleId: number) {
        try {
            const cell_id = Number(cellId);
            const rule_id = Number(ruleId);
            const url = `/rule-configs/rules/cell/${cell_id}/rule/${rule_id}`;
            const response = await this.instance.delete(url);
            notifySuccess(
                "Rules Configuration",
                "Deleted Successfully!"
            );
            return response?.status;
        } catch (error) {
            this.catchError("Rules Configuration", "deleteRule", error);
        }
    }

    async getRules(cellId: number, productId: number) {
        try {
            const cell_id = Number(cellId);
            const product_id = Number(productId);
            const response: AxiosResponse<DbApiResponse<CellRuleSet>> = await this.instance(
                `/rule-configs/rules/cell/${cell_id}/product/${product_id}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Rules Configuration", "getRules", error);
        }
    }

    async getControllersByCell(cellId: number) {
        try {
            const response: AxiosResponse<DbApiResponse<Controller>> = await this.instance.get(
                `/rule-configs/controllers/cell/${cellId}`
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError(
                "Rules Configuration",
                "getControllersByCell",
                error
            );
        }
    }

    async getMeasurementSensors(cellId: number, equipmentId: number) {
        try {
            const response: AxiosResponse<DbApiResponse<Sensor>> = await this.instance.get(
                `/rule-configs/${cellId}/sensors/${equipmentId}`
            );

            return response?.data ?? [];
        } catch (error) {
            this.catchError(
                "Rules Configuration",
                "getMeasurementSensors",
                error
            );
        }
    }

    async getMeasurementEquipment(cellId: number) {
        try {
            const response: AxiosResponse<DbApiResponse<EquipmentOutput>> = await this.instance.get(
                `/rule-configs/outputs/cell/${cellId}/equipment`
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Equipment", "getMeasurementEquipment", error);
        }
    }

    async getCaliperDetails(cellId: number, equipmentDataTagId: number) {
        try {
            const cell_id = Number(cellId);
            const equipmentDataTag_id = Number(equipmentDataTagId);
            const response = await this.instance(
                `/rule-configs/cell/${cell_id}/equipmentdatatagid/${equipmentDataTag_id}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Rules Configuration", "getCaliperDetails", error);
        }
    }

    async getCellRulesSummary(cellId: number) {
        try {
            const cell_id = Number(cellId);
            const response = await this.instance.get<{ data: FacilityLinePartsRulesEntity[], meta: { total_recipes: number, total_line_assigned_parts: number }}>(
                `/rule-configs/rules/cell/${cell_id}/rules-summary`
            );
            return response?.data ?? {};
        } catch (error) {
            console.error("Error in getCellRulesSummary: ", error);
            this.catchError("Rules Configuration", "getCellRulesSummary", error);
        }
    }
    

    // endregion Rule Configuration APIs


    async getEquipmentTemplate(reqObj: any, force_refresh: boolean = false) {
        try {
            const cell_id = reqObj?.cell?.id
            const queryParams = {cell_id, force_refresh};

            const result = await this.instance.get(`/ui-templates/`, {params: queryParams});

            if (result?.status === 200) {
                const equipmentData = result?.data?.data ?? {};
                return equipmentData;
            } else {
                notifyFailed(
                    "Signal Data",
                    "Failed to fetch Signal Data Template"
                );
                return {};
            }
        } catch (ex) {
            this.catchError("Signal Data", "getEquipmentTemplate", ex);
        }
    }

    async getRulesPlotTemplates(cellId: number, productId: number) {
        try {
            const cell_id = Number(cellId);
            const product_id = Number(productId);

            const result = await this.instance.get<{ data: PlotTemplateEntity[] }>(`/ui-templates/cell/${cell_id}/product/${product_id}/plot-templates/rules`);
            return result?.data?.data ?? [];
        } catch (error) {
            console.warn("Exception in getRulesPlotTempalate: ", error);
        }
    }

    async getLppPlotTemplates(cellId: number, productId: number) {
        try {
            const cell_id = Number(cellId);
            const product_id = Number(productId);
            const result = await this.instance.get<{ data: PlotTemplateEntity[] }>(`/ui-templates/cell/${cell_id}/product/${product_id}/plot-templates/lpp`);
            return result?.data?.data ?? [];
        } catch (error) {
            console.warn("Exception in getLppPlotTemplates: ", error);
        }
    }

    async getDqsPlotTemplates(cellId: number, runId: string, top: number = 10) {
        try {
            const cell_id = Number(cellId);
            const run_id = runId;
            const result = await this.instance.get<{ data: PlotTemplateEntity[] }>(`/ui-templates/cell/${cell_id}/run/${run_id}/plot-templates/dqs?top_n=${top}`);
            return result?.data?.data ?? [];
        } catch (error) {
            console.warn("Exception in getDqsPlotTemplates: ", error);
        }
    }

    async getUserPlotTemplates(cellId: number, productId?: number) {
        try {
            const cell_id = Number(cellId);
            if (productId) {
                const product_id = Number(productId);
                const result = await this.instance.get<{ data: PlotTemplateEntity[] }>(`/ui-templates/cell/${cell_id}/product/${product_id}/plot-templates/user-templates`);
                return result?.data?.data ?? [];
            } else {
                const result = await this.instance.get<{ data: PlotTemplateEntity[] }>(`/ui-templates/cell/${cell_id}/plot-templates/user-templates`);
                return result?.data?.data ?? [];
            }
        } catch (error) {
            console.warn("Exception in getUserPlotTemplates: ", error);
        }
    }

    async saveUserPlotTemplate(cellId: number, productId: number | null, template: any) {
        try {
            const cell_id = Number(cellId);
            if (productId) {
                const product_id = Number(productId);
                const result = await this.instance.post(`/ui-templates/cell/${cell_id}/product/${product_id}/plot-templates/user-templates`, template);
                return result?.data ?? [];
            } else {
                const result = await this.instance.post(`/ui-templates/cell/${cell_id}/plot-templates/user-templates`, template);
                return result?.data ?? [];
            }
        } catch (error) {
            console.warn("Exception in saveUserPlotTemplate: ", error);
            throw error;
        }
    }

    async shareUserPlotTemplate(cellId: number, templateId: string, sharedWith: string, body: any) {
        try {
            const result = await this.instance.post(`/ui-templates/cell/${cellId}/plot-templates/user-templates/template/${templateId}/share`, body);
            return result?.data ?? [];
        } catch (error) {
            console.warn("Exception in shareUserPlotTemplate: ", error);
            throw error;
        }
    }

    async unshareUserPlotTemplate(cellId: number, templateId: string) {
        try {
            const result = await this.instance.delete(`/ui-templates/cell/${cellId}/plot-templates/user-templates/template/${templateId}/share`);
            return result?.data ?? [];
        } catch (error) {
            console.warn("Exception in unshareUserPlotTemplate: ", error);
            throw error;
        }
    }

    async getSharedPlotTemplates(cellId: number) {
        try {
            const cell_id = Number(cellId);
            const result = await this.instance.get<{ data: SharedPlotTemplateEntity[] }>(`/ui-templates/cell/${cell_id}/plot-templates/user-templates/shared-templates`);
            return result?.data?.data ?? [];
        } catch (error) {
            console.warn("Exception in getSharedPlotTemplates: ", error);
            return [];
        }
    }

    async deleteUserPlotTemplate(cellId: number, productId: number, templateId: string) {
        try {
            const cell_id = Number(cellId);
            const product_id = Number(productId);
            const result = await this.instance.delete(`/ui-templates/cell/${cell_id}/product/${product_id}/plot-templates/user-templates/template/${templateId}`);
            return result?.data ?? [];
        } catch (error) {
            console.warn("Exception in deleteUserPlotTemplate: ", error);
            throw error;
        } 
    }

    async updateUserPlotTemplate(cellId: number, templateId: string, template: any) {
        try {
            const cell_id = Number(cellId);
            console.log('Updating cell-only template:', template);
            const result = await this.instance.put(`/ui-templates/cell/${cell_id}/plot-templates/user-templates/template/${templateId}`, template);
            return result?.data ?? [];
        } catch (error) {
            console.warn("Exception in updateUserPlotTemplate: ", error);
            throw error;
        }
    }

    async getControllableEquipByCell(cellId: number) {
        try {
            const cell_id = Number(cellId);
            const response = await this.instance.get(
                `/equipment/controllers/cell/${cell_id}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError(
                "Controllable Equipment",
                "getControllableEquipByCell",
                error
            );
        }
    }

    async getProductListByCell(cellId: number, pageNum: number) {
        try {
            const cell_id = Number(cellId);
            const newUrl = this.handlePagination(
                `/products/cell/${cell_id}`,
                pageNum
            );
            const response = await this.instance.get(newUrl);
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Products", "getProductListByCell", error);
        }
    }

    async getProductDetailsByCellAndProduct(cellId: number, productId: number) {
        try {
            const cell_id = Number(cellId);
            const newUrl = this.handlePagination(
                `/products/cell/${cell_id}/product_id/${productId}`,
                1

            );
            const response: AxiosResponse<DbApiResponse<CellProduct>> = await this.instance.get(newUrl);
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Products", "getProductListByCell", error);
        }
    }

    async getProductsByFacility(facilityId: number, pageNum: number) {
        try {
            const facility_id = Number(facilityId);
            const newUrl = this.handlePagination(
                `/products/facility/${facility_id}`,
                pageNum
            );
            const response = await this.instance.get(newUrl);
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Products", "getProductsByFacility", error);
        }
    }

    async getProducts(productNumber: string) {
        try {
            const response = await this.instance.get(
                `/parts/parts/product-number/${productNumber}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Products", "getProducts", error);
        }
    }

    async putProducts(partId: number, productRequestBody: any) {
        try {
            const id = Number(partId);
            const queryParams = { id };
            const url = `/parts/parts/part_id`;
            const response = await this.instance.put(url, productRequestBody, {
                params: queryParams,
            });
            return response?.status;
        } catch (error) {
            this.catchError("Products", "putProducts", error);
        }
    }

    async postProducts(productRequestBody: any) {
        try {
            const url = `/parts/parts`;
            const response = await this.instance.post(url, productRequestBody);
            return response?.status;
        } catch (error) {
            this.catchError("Products", "postProducts", error);
        }
    }

    async postProductFacility(productRequestBody: any) {
        try {
            const url = `/part-facility/part-facilities`;
            const response = await this.instance.post(url, productRequestBody);
            return response?.status;
        } catch (error) {
            this.catchError("Products", "postProductFacility", error);
        }
    }

    async getProductsByERPNumber(erpNumber: string) {
        try {
            const url = `/parts/parts/product-number/${erpNumber}`;
            const response = await this.instance.get(url);
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Products", "getProductsByERPNumber", error);
        }
    }

    async getCellStatusbyFacilityId(facilityId: number) {
        try {
            const url = `/cell-status/cell_status/facility/${facilityId}`;
            const response = await this.instance.get(url);
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Dashboard", "getCellStatusbyFacilityId", error);
        }
    }

    async getCellStatusByCellId(cellId: number) {
        try {
            const url = `/cell-status/cell_status/cell/${cellId}`;
            const response = await this.instance.get(url);
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Dashboard", "getCellStatusByCellId", error);
        }
    }

    async getMappingByProductsFacility(part_id: number, facility_id: number) {
        try {
            const partId = Number(part_id);
            const facilityId = Number(facility_id);
            const url = `/part-facility/part-facilities/partId/${partId}/facilityId/${facilityId}`;
            const response = await this.instance.get(url);
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Products", "getMappingByProductsFacility", error);
        }
    }

    async putProductionStatusProductsFacility(
        partFacilityId: number,
        productRequestBody: any
    ) {
        try {
            const id = Number(partFacilityId);
            const queryParams = { id };
            const url = `/part-facility/part-facilities/part_facility_id`;
            const response = await this.instance.put(url, productRequestBody, {
                params: queryParams,
            });
            return response?.status;
        } catch (error) {
            this.catchError(
                "Products",
                "putProductionStatusProductsFacility",
                error
            );
        }
    }

    async putFacilityLineAssignedParts(cellId: number, facilityLineRequestBody: any) {
        try {
            const cell_id = Number(cellId);
            const url = `/products-assigned/assigned-parts/cell/${cell_id}/product`;
            const response = await this.instance.put(
                url,
                facilityLineRequestBody
            );
            return response?.status;
        } catch (error) {
            this.catchError("Products", "putFacilityLineAssignedParts", error);
        }
    }

    async putPartAttributeValuesFacilityLine(
        partAttributeValuesFacilityLineRequestBody: any
    ) {
        try {
            const url = `/part-attributes/part-attributes`;
            const response = await this.instance.put(
                url,
                partAttributeValuesFacilityLineRequestBody
            );
            return response?.status;
        } catch (error) {
            this.catchError(
                "Products",
                "putPartAttributeValuesFacilityLine",
                error
            );
        }
    }

    async postPartAttributeValuesFacilityLine(
        partAttributeValuesFacilityLineRequestBody: any
    ) {
        try {
            const url = `/part-attributes/part-attributes`;
            const response = await this.instance.post(
                url,
                partAttributeValuesFacilityLineRequestBody
            );
            return response?.status;
        } catch (error) {
            this.catchError(
                "Products",
                "postPartAttributeValuesFacilityLine",
                error
            );
        }
    }

    async postFacilityLineAssignedParts(
        cell_id: number,
        part_id: number,
        masterFacilityLineAssignedPartsRequestBody: any
    ) {
        try {
            const cellId = Number(cell_id);
            const productId = Number(part_id);
            const url = `/products-assigned/assigned-parts/cell/${cellId}/product/${productId}`;
            const response = await this.instance.post(
                url,
                masterFacilityLineAssignedPartsRequestBody
            );
            return response?.status;
        } catch (error) {
            this.catchError("Products", "postFacilityLineAssignedParts", error);
        }
    }

    async getProductionStatus() {
        try {
            const response = await this.instance.get(
                `/products-status/enum/production-status`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Production Status", "getProductionStatus", error);
        }
    }

    async getOPCDevices() {
        try {
            const response = await this.instance.get(`/equipment/opc/devices`);
            return response?.data ?? {};
        } catch (error) {
            this.catchError("OPC Devices", "getOPCDevices", error);
        }
    }

    async getOPCConnections(cellId: number) {
        try {
            const cell_id = Number(cellId);
            const response = await this.instance.get(
                `/customer-instance/opc-connections`,
                { params: { cell_id: cell_id } }
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("OPC Connections", "getOPCConnections", error);
        }
    }

    async getInputSignalsData(cellId: number, productId: string, reqBody: any) {
        try {
            const cell_id = Number(cellId);
            const product_id = Number(productId);
            const { page_size, page_num, sort, sort_order } = reqBody;
            const queryParams = { page_size, page_num, sort, sort_order };

            const response = await this.instance.get(
                `/data_science/physics_controllable_input_signals/cell/${cell_id}/product/${product_id}`,
                { params: queryParams }
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("ML Configuration", "getInputSignalsData", error);
        }
    }

    async getLiveServices(cellId: number | string, runId: string, queryParams = {}) {
        try {
            const response = await this.instance.get(
                `/dashboards/cell/${cellId}/services/run/${runId}`,
                {
                    params: queryParams,
                }
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Dashboard", "getDashboardUrlList", error);
        }
    }

    async getLiveServicesNoRun(cellId: number, queryParams = {}) {
        try {
            const response = await this.instance.get(
                `/dashboards/cell/${cellId}/services/no-run`,
                {
                    params: queryParams,
                }
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Dashboard", "getDashboardUrlList", error);
        }
    }

    async getDashboardURLList(cellId: number | string, runId: string, queryParams: any) {
        try {
            let response = await this.instance.get(
                `/dashboards/cell/${cellId}/run/${runId}`,
                {
                    params: queryParams,
                }
            );

            // Alert user if no dashboards are found / returned-
            return response?.data?.data ?? [];
        } catch (error) {
            this.catchError("Dashboard", "getDashboardUrlList", error);
        }
    }

    async getNoRunDashboards(cellId: number, queryParams: any) {
        try {
            let response = await this.instance.get(
                `/dashboards/cell/${cellId}/no-run`,
                {
                    params: queryParams,
                }
            );

            // Alert user if no dashboards are found / returned-
            return response?.data?.data ?? [];
        } catch (error) {
            this.catchError("Dashboard", "getDashboardUrlList", error);
        }
    }

    async getOutputSignalsData(cellId: number, productId: number, reqBody: any) {
        try {
            const cell_id = Number(cellId);
            const product_id = Number(productId);
            const { page_size, page_num, sort, sort_order } = reqBody;
            const queryParams = { page_size, page_num, sort, sort_order };

            const response = await this.instance.get<{ data: PhysicsSignalEntity[], meta: any}>(
                `/data_science/physics_output_signals/cell/${cell_id}/product/${product_id}`,
                { params: queryParams }
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("ML Configuration", "getOutputSignalsData", error);
        }
    }

    async getDashboardsByRunId(reqObj: any) {
        try {
            const { run_id, cell_id } = reqObj;
            const response = await this.instance.get(
                `/dashboards/cellid/${cell_id}/runid/${run_id}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Dashboard", "getDashboardsByRunId", error);
        }
    }

    async getRunReportPDFUrl(cell_id: number, run_id: string, pdfPath?: string) {
        try {
            const params = pdfPath ? { pdf_path: pdfPath } : {};
            const response = await this.instance.get(
                `/reporting/cell/${cell_id}/run/${run_id}/summary/pdf`,
                { params }
            );
            return response ?? {};
        } catch (error: any) {
            const errorMessage = { message: error.response.data.detail };
            this.catchError(
                "Historical Runs",
                "getRunReportPDFUrl",
                errorMessage
            );
        }
    }

    async getDefectJpgUrl(cell_id: number | string, run_id: string) {
        try {
            const cellId = Number(cell_id);
            const response = await this.instance.get(
                `/reporting/cell/${cellId}/run/${run_id}/defect/jpg/url`
            );
            return response?.data ?? [];
        } catch (error: any) {
            const errorMessage = { message: error.response.data.detail };
            this.catchError(
                "Historical Runs",
                "getDefectJpgUrl",
                errorMessage
            );
        }
    }

    async getShapelyChartPNGUrl(cell_id: number, run_id: string) {
        try {
            const response = await this.instance.get(
                `/reporting/cell/${cell_id}/run/${run_id}/shapely/png`
            );
            return response ?? {};
        } catch (error: any) {
            const errorMessage = { message: error.response.data.detail };
            this.catchError(
                "Historical Runs",
                "getShapelyChartPNGUrl",
                errorMessage
            );
        }
    }

    async getDataScienceDPK(cellId: number, runId: number) {
        try {
            const cell_id = Number(cellId);
            const run_id = Number(runId);

            const response = await this.instance.get(
                `/data_science/dpk/cell/${cell_id}/run/${run_id}`
            );
            return response?.data ?? {};
        } catch (error: any) {
            const errorMessage = { message: error.response.data.detail };
            this.catchError(
                "Historical Runs",
                "getDataScienceDPK",
                errorMessage
            );
        }
    }

    // region Utilization & OEE APIs
    
    // New Facility Utilization APIs
    async getFacilityMonthlyUtilization(facilityId: number, startTime: number, endTime: number) {
        try {
            const queryParams = { startTime, endTime };
            const url = `/reporting/oee/utilization/facility/${facilityId}/monthly/aggregate`;
            const response = await this.instance.get<{ time: string, name: string, on_percentage: number, sum_hours: number }[]>(url, { params: queryParams });
            console.log("getFacilityMonthlyUtilization", response);
            return response?.data ?? [];
        } catch (error) {
            this.catchError(
                "Facility Monthly Utilization",
                "getFacilityMonthlyUtilization",
                error
            );
        }
    }

    async getFacilityMonthlyPerCell(facilityId: number, startTime: number, endTime: number) {
        try {
            const queryParams = { startTime, endTime };
            const url = `/reporting/oee/utilization/facility/${facilityId}/monthly/per_cell`;
            const response = await this.instance.get<{ time: string, name: string, on_percentage: number, sum_hours: number }[]>(url, { params: queryParams });
            return response?.data ?? [];
        } catch (error) {
            this.catchError(
                "Facility Monthly Per Cell",
                "getFacilityMonthlyPerCell",
                error
            );
        }
    }

    async getFacilityWeeklyPerCell(facilityId: number, startTime: number, endTime: number) {
        try {
            const queryParams = { startTime, endTime };
            const url = `/reporting/oee/utilization/facility/${facilityId}/weekly/per_cell`;
            const response = await this.instance.get<{ time: string, name: string, on_percentage: number, sum_hours: number }[]>(url, { params: queryParams });
            return response?.data ?? [];
        } catch (error) {
            this.catchError(
                "Facility Weekly Per Cell",
                "getFacilityWeeklyPerCell",
                error
            );
        }
    }

    async getCellWeeklyPerProduct(cellId: number, startTime: number, endTime: number) {
        try {
            const queryParams = { startTime, endTime };
            const url = `/reporting/oee/utilization/cell/${cellId}/weekly/per_product`;
            const response = await this.instance.get<{ time: string, description: string, on_percentage: number, sum_hours: number }[]>(url, { params: queryParams });
            return response?.data ?? [];
        } catch (error) {
            this.catchError(
                "Cell Weekly Per Product",
                "getCellWeeklyPerProduct",
                error
            );
        }
    }

    async getLLUtilizationData(startDate = "", endDate = "", cellId = "") {
        try {
            const queryParams = {
                startDate,
                endDate,
            };
            // Construct the full URL with query string
            const url = `/reporting/cell/${cellId}/shifts/summary`;

            const response = await this.instance.get<{ date: string, shift_name: string, sum_hours: number, on_percentage: number }[]>(url, {
                params: queryParams,
            });
            return response?.data ?? [];
        } catch (error) {
            this.catchError(
                "Liveline Utilization",
                "getLLUtilizationData",
                error
            );
        }
    }

    async getUtilizationCell(cellId = "", startTime = "", endTime = "") {
        try {
            const queryParams = {
                startTime,
                endTime,
            };
            // Construct the full URL with query string
            const url = `/reporting/utilization/cell/${cellId}`;
            // Append the isLatest query parameter to the endpoint URL
            const response = await this.instance.get(url, {
                params: queryParams,
            });
            return response?.data?.data ?? [];
        } catch (error) {
            this.catchError(
                "Liveline Utilization by Cell",
                "getUtilizationCell",
                error
            );
        }
    }

    async getRunSummary(cellId: number, runIds: number[]): Promise<ReportingRunSummary[]> {
        try {
            const response = await this.instance.get<ReportingRunSummary[]>(
                `/reporting/cell/${cellId}/run-summary`,
                { params: { run_ids: runIds.join(',') } }
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Reporting", "getRunSummary", error);
            return [];
        }
    }

    async getCellShifts(cellId: number) {
        try {
            const response = await this.instance.get(
                `/oee/cell/${cellId}/shifts`
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Cell Shifts", "getCellShifts", error);
        }
    }

    // This API return a list of all scrap
    async getScrapReasons(cellId: number) {
        try {
            const url = `/oee/recorded_scrap/cell/${cellId}`;

            const response = await this.instance.get(url);
            return response?.data ?? [];
        } catch (error) {
            this.catchError(
                "Cell Timer get scrap reasons",
                "getScrapReasons",
                error
            );
        }
    }

    // endregion Utilization & OEE APIs

    // region Auth and User Management
    async createUser(createUserBody: Signup) {
        try {
            const url = "/signup";
            const response = await this.instance.post(url, createUserBody);
            return response;
        } catch (error) {
            this.catchError("Admin User", "createUser", error);
        }
    }

    async getRoles() {
        try {
            const response = await this.instance.get<AxiosResponse<Role[]>>(`/roles`);
            return response.data;
        } catch (error) {
            this.catchError("Roles", "getRoles", error);
        }
    }

    async getUsers() {
        try {
            const response = await this.instance.get<AxiosResponse<User[]>>(`/users`);
            return response;
        } catch (error) {
            this.catchError("Users", "getUsers", error);
        }
    }

    async getUserFacilityRoles() {
        try {
            const response = await this.instance.get<AxiosResponse<UserFacilityRole[]>>(`/users/facility-roles`);
            const data = response.data;
            return data;
        }
        catch (error) {
            this.catchError("User Facility Roles", "getUserFacilityRoles", error);
        }
    }


    async getFacilityUsers() {
        try {
            const response = await this.instance.get(`/facility-users`);
            return response;
        } catch (error) {
            this.catchError("FacilityUsers", "getFacilityUsers", error);
        }
    }

    async getUserDetails(userId: string) {
        try {
            const response = await this.instance.get<{ data: User }>(`/users/${userId}`);
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Users", "getUserDetails", error);
        }
    }

    async updateUser(id: string, body: any) {
        try {
            const response = await this.instance.put(`/users/${id}`, body);
            return response;
        } catch (error) {
            this.catchError("Users", "updateUsers", error);
        }
    }

    async addFacilityUser(body: FacilityUserRole) {
        try {
            const response = await this.instance.post(
                `/facility-users`,
                body
            );
            return response;
        } catch (error) {
            this.catchError("FacilityUsers", "updateFacilityUsers", error);
        }
    }

    async updateFacilityUser(facilityUserId: string, body: any) {
        try {
            const response = await this.instance.put(
                `/facility-users/${facilityUserId}`,
                body
            );
            return response;
        } catch (error) {
            this.catchError("FacilityUsers", "updateFacilityUsers", error);
        }
    }

    async removeFacilityUser(facilityUserId: string) {
        try {
            const response = await this.instance.delete(
                `/facility-users/${facilityUserId}`
            );
            return response;
        } catch (error) {
            this.catchError("FacilityUsers", "updateFacilityUsers", error);
        }
    }

    async getFacilityUserByUserId(id: string) {
        try {
            const response = await this.instance.get(
                `/facility-users/user/${id}`
            );
            return response;
        } catch (error) {
            this.catchError("FacilityUser", "getFacilityUser", error);
        }
    }
    // endregion Auth and User Management

    // This API writes an event when a rule is updated-
    async createRuleEvent(cell_id: number, requestBody: any) {
        try {
            const cellId = Number(cell_id);
            const url = `/events/facilityline/cell/${cellId}`;
            await this.instance.post(url, requestBody);
            //return response?.status; // this action is not for the users benefit-
        } catch (error) {
            this.catchError("Rules Configuration", "createRuleEvent", error);
        }
    }

    // This API fetches the last run in all the cells for the selected facility -
    async getLastRun(facility: number) {
        try {
            const response = await this.instance.get(
                `/model-metrics/${facility}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Runs", "getLastRun", error);
        }
    }

    // This API fetches the runs for the selected facility and cell -
    async getRunsForCells(facility: number, cell: number) {
        try {
            const response = await this.instance.get(
                `/model-metrics/${facility}/cell/${cell}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Runs", "getRunsForCells", error);
        }
    }

    // region Data Explorer APIs

    async getEquipmentData(cellId: number, equipmentDataTagId: number, startDate: string, endDate: string) {
        try {
            const cell_id = Number(cellId);
            const equipmentDataTag_id = Number(equipmentDataTagId);
            const queryParams = { startDate, endDate };
            const response = await this.instance.get<TimeSeriesDataPoint[]>(
                `/equipment/equipment_tag/cell/${cell_id}/equipment_data_tag/${equipmentDataTag_id}/data`,
                { params: queryParams }
            );
            const results = response?.data ?? [];
            return results;
        } catch (error) {
            this.catchError("Get Equipment Data", "getEquipmentData", error);
            return [];
        }
    }

    async getEquipmentDataAgg(
        cellId: number,
        equipmentDataTagId: number,
        startDate: string,
        endDate: string,
        bucketSeconds: number,
        aggFunction: string = 'avg',
        options: { throwOnError?: boolean } = {}
    ) {
        try {
            const cell_id = Number(cellId);
            const equipmentDataTag_id = Number(equipmentDataTagId);
            const queryParams = { startDate, endDate, bucketSeconds, aggFunction };
            const response = await this.instance.get<TimeSeriesDataPoint[]>(
                `/equipment/equipment_tag/cell/${cell_id}/equipment_data_tag/${equipmentDataTag_id}/data/aggregated`,
                { params: queryParams }
            );
            const results = response?.data ?? [];
            return results;
        } catch (error) {
            // Opt-in propagation so callers (e.g. the throttled time-series queue)
            // can classify axios errors as transient and retry with backoff.
            if (options.throwOnError) throw error;
            this.catchError("Get Equipment Data", "getEquipmentData", error);
            return [];
        }
    }

    // Synthetic measurement-metric series (error / weighted_error / shrinkage / new_target)
    // extracted from the stored rules-controller measurement envelopes — the Advanced
    // Controllers error/shrinkage chart's historical source. Same {time_stamp, value}
    // shape as getEquipmentDataAgg; aggFunction 'last' returns the newest value per bucket
    // (the natural read for the slow-moving shrink_factor/new_target series).
    async getMeasurementMetricAgg(
        cellId: number,
        metric: 'error' | 'weighted_error' | 'shrinkage' | 'new_target',
        startDate: string,
        endDate: string,
        bucketSeconds: number,
        aggFunction: string = 'avg',
        ruleNumber?: number,
        measurementNumber?: number,
    ) {
        try {
            const cell_id = Number(cellId);
            const queryParams: Record<string, string | number> = { startDate, endDate, bucketSeconds, aggFunction };
            if (ruleNumber !== undefined) queryParams.ruleNumber = ruleNumber;
            if (measurementNumber !== undefined) queryParams.measurementNumber = measurementNumber;
            const response = await this.instance.get<TimeSeriesDataPoint[]>(
                `/equipment/equipment_tag/cell/${cell_id}/measurement_metric/${metric}/data/aggregated`,
                { params: queryParams }
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Get Measurement Metric", "getMeasurementMetricAgg", error);
            return [];
        }
    }

    // Controls control-state series (controller_active 1/0, etc.) extracted from the
    // top-level ControlState block of the stored rules-controller measurement envelopes
    // (LT-3157) — the Advanced Controllers Controls-ON ribbon's historical backfill.
    // Same {time_stamp, value} shape as getMeasurementMetricAgg; aggFunction 'last'
    // returns the newest value per bucket, the natural read for the step-rendered ribbon.
    async getControlStateAgg(
        cellId: number,
        field: string,
        startDate: string,
        endDate: string,
        bucketSeconds: number,
        aggFunction: string = 'last',
    ) {
        try {
            const cell_id = Number(cellId);
            const queryParams: Record<string, string | number> = { startTime: startDate, endTime: endDate, bucketSeconds, aggFunction };
            const response = await this.instance.get<TimeSeriesDataPoint[]>(
                `/equipment/equipment_tag/cell/${cell_id}/control_state/${field}/data/aggregated`,
                { params: queryParams }
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Get Control State", "getControlStateAgg", error);
            return [];
        }
    }

    async getHistoricalRunStats(cellId: number, productId: number, start: number, end: number): Promise<{ data: {Run: Run, keystats: KeyStats, dpk_id: string }[]}> {
        try {
            const cell_id = Number(cellId);
            const product_id = Number(productId);
            const response = await this.instance.get<{ data: {Run: Run, keystats: KeyStats, dpk_id: string }[]}>(
                `/runs/cell/${cell_id}/product/${product_id}/runs/statistics`,  
                { params: { start, end } }
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Get Historical Run Stats", "getHistoricalRunStats", error);
            throw error;
        }
    }

    async getEquipmentDataAggBatch(cellId: number, equipmentDataTagIds: number[], startDate: string, endDate: string, bucketSeconds: number) {
        try {
            const cell_id = Number(cellId);
            const body = { startDate, endDate, bucketSeconds, equipmentDataTagIds};
            const response = await this.instance.post<{ cell_id: number, data: Record<number, {time_stamp: any, value: number | null }[]>}>(
                `/equipment/equipment_tag/cell/${cell_id}/equipment_data_tags/data/aggregated/batch`, body
            );
            const results = response?.data ?? [];
            return results;
        } catch (error) {
            this.catchError("Get Equipment Data", "getEquipmentData", error);
        }
    }

    async getCellEquipmentActuals(cellId: number) {
        try {
            const cell_id = Number(cellId);
            const response = await this.instance.get(
                `/equipment/equipment_tag/cell/${cell_id}/actuals`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError(
                "Equipment Actuals",
                "getCellEquipmentActuals",
                error
            );
        }
    }

    async getCellExpertSignals(cellId: number, product?: number) {
        try {
            const cell_id = Number(cellId);
            let params = undefined;
            if (product) {
                params = { product_id: product };
            }
            const response = await this.instance.get<{data: PhysicsSignalEntity[], meta: any}>(
                `/equipment/equipment_tag/cell/${cell_id}/expert`,
                { params: params }
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Expert Signals", "getCellExpertSignals", error);
        }
    }

    async getCellExpertRelatedSignals(cellId: number, product: number) {
        try {
            const cell_id = Number(cellId);
            let params = undefined;
            if (product) {
                params = { product_id: product };
            }
            const response = await this.instance.get<{data: EquipmentTagSimpleView[]}>(
                `/equipment/equipment_tag/cell/${cell_id}/expert/related`,
                { params: params }
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Expert Related Signals", "getCellExpertRelatedSignals", error);
        }
    }

    async getCellLineCommonDataSignals(cellId: number) {
        try {
            const cell_id = Number(cellId);
            const response = await this.instance.get<{ data: EquipmentTagSimpleView[], meta: any}>(
                `/equipment/equipment_tag/cell/${cell_id}/line-common-data`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Common Data Signals", "getCellLineCommonDataSignals", error);
        }
    }

    async getCellEquipmentTree(cellId: number) {
        try {
            const cell_id = Number(cellId);
            const response = await this.instance.get<{ equipment: EquipmentTreeEntity[]}>(
                `/equipment/equipment_tag/cell/${cell_id}/equipment/tree`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("Equipment Tree", "getCellEquipmentTree", error);
        }
    }

    async getEquipmentDataTagName(cellId: number, name: string) {
        try {
            const cell_id = Number(cellId);
            const queryParms = { name };
            const response = await this.instance.get(
                `/equipment/equipment_tag/cell/${cell_id}/equipment_data_tag`, { params: queryParms }
            );
            const results = response?.data?.data[0] ?? {};
            return results;
        } catch (error) {
            this.catchError("Get Equipment Data Tag by Name", "getEquipmentDataTagName", error);
        }
    }
    async getExpressionData(cellId: number, expression: string, startDate: string, endDate: string, bucketSeconds: number, aggFunction: string = 'avg') {
        // Throws the original axios error so callers (e.g. the throttled
        // time-series queue) can classify failures and retry transients.
        const cell_id = Number(cellId);
        const body = { expression, startTime: startDate, endTime: endDate, bucketSeconds, aggFunction };
        const response = await this.instance.post<{ cell_id: number; expression: string; data: { time_stamp: any; value: number | null }[] }>(
            `/equipment/equipment_tag/cell/${cell_id}/data/expression`, body
        );
        return response?.data?.data ?? [];
    }

    // endregion Data Explorer APIs

    async getPerformanceURL(facilityId: number) {
        try {
            const response = await this.instance.get(
                `/reporting/utilization/facility/${facilityId}`
            );
            return response?.data ?? {};
        } catch (error: any) {
            const errorMessage = { message: error?.response.data.detail };
            this.catchError(
                "Analytics",
                "getPerformanceURL",
                errorMessage
            );
        }
    }

    // region Liveline Physics Package APIs

    async getLPPs(facilityId: number, cellId: number, partId?: number) {
        try {
            const response = await this.instance.get<LivelinePhysicsPackageRecord[]>(
                `/data_science/lpp/facility/${facilityId}/cell/${cellId}`, { params: { productId: partId } }
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LPPs", "getLPPs", error);
        }
    }

    async getLPP(facilityId: number, lppId: string) {
        try {
            const response = await this.instance.get<LivelinePhysicsPackageRecord>(
                `/data_science/lpp/facility/${facilityId}/lpp/${lppId}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LPP", "getLPP", error);
        }
    }

    async getLPPPerformance(cellId: number, lppId: string) {
        try {
            const response = await this.instance.get<{ data: LPPPerformance[], meta: any}>(
                `/data_science/lpp/cell/${cellId}/performance/lpp/${lppId}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LPP", "getLPPPerformance", error);
        }
    }

    async getCellLPPPerformance(cellId: number, daysAgo: number) {
        try {
            const response = await this.instance.get<{ data: {lpp_performance: LPPPerformance, run_summary: Run}[], meta: any}>(
                `/data_science/lpp/cell/${cellId}/performance/latest`, { params: { "days_back": daysAgo } }
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LPP", "CellLPPPerformance", error);
        }
    }

    async getCellLppTrainingRequests(cellId: number) {
        try {
            const response = await this.instance.get<{ data: LPPTrainingRequest[], meta: any}>(
                `/data_science/lpp/cell/${cellId}/training_requests`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LPP", "getCellLppTrainingRequests", error);
        }
    }

    async getFacilityLppTrainingBacktestUrl(facilityId: number, requestId: string) {
        try {
            const response = await this.instance.get<{ url: string}>(
                `/data_science/lpp/facility/${facilityId}/training_requests/${requestId}/backtest/url`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LPP", "getFacilityLppTrainingBacktestUrl", error);
        }
    }


    async getProductionLPP(facilityId: number, cellId: number, partId: number) {
        try {
            const response = await this.instance.get<LivelinePhysicsPackageRecord>(
                `/data_science/lpp/cell/${cellId}/product/${partId}/production`,
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LPP", "getProductionLPP", error);
        }
    }

    async getDQSRunResult(cellId: number, runId: string) {
        try {
            const response = await this.instance.get<DQSRunResult>(
                `/data_science/dqs/cell/${cellId}/run/${runId}`
            );
            console.log(response?.data);
            return response?.data ?? {};
        } catch (error) {
           console.log(error);
        }
    }

    async getProductionLPPCoverage(cellId: number) {
        try {
            const response = await this.instance.get<{ cell_id: number, part_id: number, product_description: string, production_lpp?: string }[]>(
                `/data_science/lpp/cell/${cellId}/production/coverage`,
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LPP", "getProductionLPPCoverage", error);
        }
    }

    // endregion Liveline Physics Package APIs

    // region Liveline Controls Package APIs
    async getLCPs(facilityId: number, cellId: number) {
        try {
            const response = await this.instance.get<LivelineControlsPackageRecord[]>(
                `/data_science/lcp/facility/${facilityId}/cell/${cellId}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LCPs", "getLCPs", error);
        }
    }

    async getLCP(cellId: number, lcpId: string) {
        try {
            const response = await this.instance.get<LivelineControlsPackageRecord>(
                `/data_science/lcp/cell/${cellId}/lcp/${lcpId}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LCP", "getLCP", error);
        }
    }

    async getCellLcpTrainingRequests(cellId: number) {
        try {
            const response = await this.instance.get<{ data: LCPTrainingRequest[], meta: any}>(
                `/data_science/lcp/cell/${cellId}/training_requests`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LCP", "getCellLcpTrainingRequests", error);
        }
    }

    async getFacilityLcpTrainingBacktestUrl(facilityId: number, requestId: string) {
        try {
            const response = await this.instance.get<{ url: string}>(
                `/data_science/lcp/facility/${facilityId}/training_requests/${requestId}/backtest/url`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LCP", "getFacilityLcpTrainingBacktestUrl", error);
        }
    }

    async getLCPPerformance(cellId: number, lcpId: string) {
        try {
            const response = await this.instance.get<{ data: LCPPerformance[], meta: any}>(
                `/data_science/lcp/cell/${cellId}/performance/lcp/${lcpId}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LCP", "getLCPPerformance", error);
        }
    }

    async getProductionLCP(facilityId: number, cellId: number, partId: number) {
        try {
            const response = await this.instance.get<LivelineControlsPackageRecord>(
                `/data_science/lcp/cell/${cellId}/product/${partId}/production`,
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LCP", "getProductionLCP", error);
        }
    }

    async createLcpTrainingRequest(facilityId: number, payload: any) {
        try {
            const response = await this.instance.post(
                `/data_science/lcp/facility/${facilityId}/training_request`,
                payload
            );
            notifySuccess(
                "LCP Training Request",
                "Training request created successfully"
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LCP", "createLcpTrainingRequest", error);
            throw error;
        }
    }

    async assignDpkToLcpTrainingRequest(facilityId: number, requestId: string, dpkId: string) {
        try {
            const response = await this.instance.post(
                `/data_science/lcp/facility/${facilityId}/training_request/${requestId}/dpk/${dpkId}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LCP", "assignDpkToLcpTrainingRequest", error);
        }
    }

    async assignDpkToLcpTestingRequest(facilityId: number, requestId: string, dpkId: string) {
        try {
            const response = await this.instance.post(
                `/data_science/lcp/facility/${facilityId}/testing_request/${requestId}/dpk/${dpkId}`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LCP", "assignDpkToLcpTestingRequest", error);
        }
    }

    async queueLcpTrainingRequest(facilityId: number, requestId: string) {
        try {
            const response = await this.instance.post(
                `/data_science/lcp/facility/${facilityId}/training_request/${requestId}/queue`
            );
            return response?.data ?? {};
        } catch (error) {
            this.catchError("LCP", "queueLcpTrainingRequest", error);
        }
    }

    // endregion Liveline Controls Package APIs

    async getMaterialsByCell(cellId: number) {
        try {
            const response = await this.instance.get(
                `/oee/materials/cell/${cellId}`
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Materials by Cell", "getMaterialsByCell", error);
        }
    }

    async getMaterialUsageByCell(
        cellId: number, 
        time_start?: string | undefined, 
        time_end?: string | undefined, 
        usage_only?: boolean) 
        {
        try {
            const queryParams = { time_start, time_end, usage_only };
            const response = await this.instance.get(
                `/process_data/raw_material/cell/${cellId}`,
                { params: queryParams }
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Material Usage by Cell", "getMaterialUsageByCell", error);
        }
    }

    async getMaterialUsageByFacility(
        facilityId: number 
    )
        {
        try {
            const response = await this.instance.get(
                `/process_data/raw_material/usage/facility/${facilityId}`
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Material Usage by Facility", "getMaterialUsageByFacility", error);
        }
    }

    async getMaterialsInUseByCell(cellId: number) {
        try {
            const response = await this.instance.get(
                `/process_data/raw_material/inuse/cell/${cellId}`
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Material InUse by Cell", "getMaterialInUseByCell", error);
        }
    }

    async getMaterialsInUseByFacility(facilityId: number) {
            try {
                const response = await this.instance.get(
                    `/process_data/raw_material/inuse/facility/${facilityId}`
                );
                return response?.data ?? [];
            } catch (error) {
                this.catchError("Material InUse by Facility", "getMaterialsInUseByFacility", error);
            }
    }

    async postRawMaterialUnit(cellId: number, requestBody: any) {
        try {
            const queryParams = { cellId };
            const url = "/process_data/raw_material/unit";
            const response = await this.instance.post(url, requestBody, {params: queryParams});
            return response;
        } catch (error) {
            this.catchError(
                "Raw Material Unit",
                "postRawMaterialUnit",
                error
            );
        }
    }

    async putRawMaterialUnit(cellId: number, requestBody: any) {
        try {
            const queryParams = { cellId };
            const url = "/process_data/raw_material/unit";
            const response = await this.instance.put(url, requestBody, {params: queryParams});
            return response;
        } catch (error) {
            this.catchError(
                "Raw Material Unit",
                "putRawMaterialUnit",
                error
            );
        }
    }

    async postRawMaterialUsage(cellId: number, requestBody: any) {
        try {
            const queryParams = { cellId };
            const url = "/process_data/raw_material/usage";
            const response = await this.instance.post(url, requestBody, {params: queryParams});
            return response;
        } catch (error) {
            this.catchError(
                "Raw Material Usage",
                "postRawMaterialUsage",
                error
            );
        }
    }

    async putRawMaterialUsage(cellId: number, requestBody: any) {
        try {
            const queryParams = { cellId };
            const url = "/process_data/raw_material/usage";
            const response = await this.instance.put(url, requestBody, {params: queryParams});
            return response;
        } catch (error) {
            this.catchError(
                "Raw Material Usage",
                "putRawMaterialUsage",
                error
            );
        }
    }

    async getEventDefect(cell_id: number | string, time_start: string, time_end: string) {
        try {
            const queryParams = { time_start, time_end }
            const cellId = Number(cell_id);
            const url = `/events/defect/cell/${cellId}`;
            const response = await this.instance.get(url, {params: queryParams});
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Event Defect", "getEventDefect", error);
        }
    }

    async putEventDefect(
        cell_id: number | string, 
        event_id: number, 
        equipment_data_tag_id: number, 
        time_stamp: string, 
        is_true_defect: boolean | null, 
        is_predicted: boolean, 
        body: any) 
        {
        try {
            const queryParams = { event_id, equipment_data_tag_id, time_stamp: new Date(time_stamp).toISOString(), is_true_defect, is_predicted}
            const cellId = Number(cell_id);
            const url = `/events/defect/cell/${cellId}`;
            const response = await this.instance.put(url, body, {params: queryParams});
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Event Defect", "putEventDefect", error);
        }
    }

    async getCriticalSignals(cellId: number, productId: number) {
        try {
            const response = await this.instance.get(
                `/controls/recipe/critical_signals/cellId/${cellId}/productId/${productId}`
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Critical Signal(s)", "getCriticalSignals", error);
        }
    }

    async getCriticalSignal(cellId: number, Id: number) {
        try {
            const response = await this.instance.get(
                `/controls/recipe/critical_signals/cellId/${cellId}/id/${Id}`
            );
            return response?.data ?? [];
        } catch (error) {
            this.catchError("Critical Signal", "getCriticalSignal", error);
        }
    }

    async putCriticalSignal(cellId: number, Id: number, requestBody: any) {
        try {
            const url = `/controls/recipe/critical_signals/cellId/${cellId}/id/${Id}`;
            const response = await this.instance.put(
                url,
                requestBody
            );
            return response?.status;
        } catch (error) {
            this.catchError("Critical Signal", "putCriticalSignal", error);
        }
    }

    async postCriticalSignal(cellId: number, requestBody: any) {
        try {
            const url = `/controls/recipe/critical_signals/cellId/${cellId}`;
            const response = await this.instance.post(url, requestBody);
            return response?.status;
        } catch (error) {
            this.catchError(
                "Critical Signal",
                "postCriticalSignal",
                error
            );
        }
    }

    async deleteCriticalSignal(cellId: number, Id: number) {
        try {
            const response = await this.instance.delete(
                `/controls/recipe/critical_signals/cellId/${cellId}/id/${Id}`
            );
            return response?.status;
        } catch (error) {
            this.catchError("Critical Signal", "deleteCriticalSignal", error);
        }
    }
    // endregion Liveline Physics Package APIs

    // Cell Layout endpoints
    async asJson<T>(res: Response, op: string): Promise<T> {
        if (!res.ok) {
            let detail = '';
            try {
                const body = await res.json();
                detail = body?.detail ? `: ${body.detail}` : '';
            } catch {
            /* non-JSON error body */
            }
            throw new Error(`${op} failed (${res.status})${detail}`);
        }
        return res.json() as Promise<T>;
    }

    async saveLayout(layout: LayoutPayload): Promise<LayoutDetail> {
        if (layout.id) {
            const url = `/cells/cell-layout/cell/${layout.cellId}/layout/${layout.id}`;
            const response = await this.instance.put(url, layout);
            return response?.data;
        } else {
            const url = `/cells/cell-layout/cell/${layout.cellId}`;
            const response = await this.instance.post(url, layout);
            return response?.data;
        }
    }

    async getLayout(cellId: number): Promise<LayoutDetail> {
        const url = `/cells/cell-layout/cell/${cellId}`;
        const response = await this.instance.get(
                url
        );
        return response?.data ?? [];
    }

    async deleteLayout(cellId: number): Promise<void> {
        const url = `/cells/cell-layout/cell/${cellId}`;
        await this.instance.delete(url);
    }

    async getEquipmentSignals(cellId: number): Promise<Record<number, EquipmentUITemplateKafkaConnection[]>> {
        const url = `/cells/cell-layout/cell/${cellId}/signals`;
        const response = await this.instance.get(url);
        return response?.data ?? {};
    }

    // Customer-master-scoped, not per-cell -- see ll_console_server's
    // equipment-layout-images-router.ts. ll_db_api's own list response wraps
    // as {data: [...], meta: {...}}, but the console_server route already
    // unwraps that (`res.json(response.data)`) before it reaches here, so
    // this axios response's `.data` is already the plain row array.
    async listEquipmentLayoutImages(): Promise<EquipmentLayoutImageEntity[]> {
        const url = `/equipment-layout-images`;
        const response = await this.instance.get(url);
        return response?.data ?? [];
    }

    async uploadEquipmentLayoutImage(file: File, name: string, equipmentTypeId?: number | null): Promise<EquipmentLayoutImageEntity> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', name);
        if (equipmentTypeId != null) formData.append('equipmentTypeId', String(equipmentTypeId));
        const response = await this.instance.post(`/equipment-layout-images`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response?.data;
    }

    // Pass equipmentTypeId: null (not omitted) to clear an existing mapping --
    // ll_db_api distinguishes "field explicitly null" (clear) from "field
    // omitted" (leave untouched) via Pydantic's model_fields_set.
    async updateEquipmentLayoutImage(id: number, body: { name?: string; equipmentTypeId?: number | null }): Promise<void> {
        await this.instance.put(`/equipment-layout-images/${id}`, body);
    }

    async deleteEquipmentLayoutImage(id: number): Promise<void> {
        await this.instance.delete(`/equipment-layout-images/${id}`);
    }

    async replaceEquipmentLayoutImage(id: number, file: File): Promise<void> {
        const formData = new FormData();
        formData.append('file', file);
        await this.instance.put(`/equipment-layout-images/${id}/image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }
}

